import React, { useState, useEffect, useMemo } from 'react';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { CheckCircle } from 'react-bootstrap-icons';

// Context & Custom Hooks
import { useLanguage } from '../../../../../API/Context/LanguageContext';
import { useUser } from '../../../../../API/Context/UserContext';
import { useUserMeSkills } from '../../../../../API/Hooks/useUserMeSkills';

// Services & Config
import SkillsService from '../../../../../API/Services/SkillsService';
import SpecialtiesService from '../../../../../API/Services/SpecialtiesService';
import VocabProMenu from '../../../../../ConfigFiles/multilingual/VocabProMenu';
import { VocabButtons as btn_vocab } from '../../../../../ConfigFiles/multilingual/VocabButtons';

import { SpecialtySubSection } from '../../../LoggedUserGeneral/ProOnboarding/Steps/Widgets/SpecialtySubSection';
import { SkillsBadgeSection } from '../../../LoggedUserGeneral/ProOnboarding/Steps/Widgets/SkillsBadgeSection';
import type { SkillMetadata } from '../../../LoggedUserGeneral/ProOnboarding/Steps/StepSkills';

import type { SkillRead, PartialProfessionalProfileUpdate } from '../../../../../Types/schema-helpers';

interface SkillsWidgetProps {
  onSaveSuccess?: () => void;
}

interface UserSkillsState {
  specialty_id: string | null;
  subspecialty_id: string | null;
  skills: string[];
}

interface SkillsMetadataState {
  specialties: Record<string, { name: string }>;
  subspecialties: Record<string, { name: string }>;
  skills: Record<string, SkillMetadata>;
}

export const SkillsWidget: React.FC<SkillsWidgetProps> = ({ onSaveSuccess }) => {
  const { t } = useLanguage();
  const { user, updateProfileField } = useUser();
  const { updateSkills, loading: isSavingHook } = useUserMeSkills();

  // Instantiate memoized services
  const skillsService = useMemo(() => new SkillsService(), []);
  const specService = useMemo(() => new SpecialtiesService(), []);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form State
  const [skillsData, setSkillsData] = useState<UserSkillsState>({
    specialty_id: null,
    subspecialty_id: null,
    skills: [],
  });

  // Display Metadata Cache
  const [metadata, setMetadata] = useState<SkillsMetadataState>({
    specialties: {},
    subspecialties: {},
    skills: {},
  });

  // Hydrate state from existing user profile context
  useEffect(() => {
    if (user?.professional) {
      const prof = user.professional;

      // 1. Extract IDs from full DTO objects
      const specObj = prof.specialty as any;
      const subObj = prof.subspecialty as any;

      const specId = specObj ? (specObj._id || specObj.id || null) : null;
      const subId = subObj ? (subObj._id || subObj.id || null) : null;

      // 2. Extract skill IDs and hydrate skill badge metadata
      const rawSkills = Array.isArray(prof.skills)
        ? prof.skills.map((s: any) => (typeof s === 'string' ? s : s.id || s._id))
        : [];

      setSkillsData({
        specialty_id: specId,
        subspecialty_id: subId,
        skills: rawSkills,
      });

      // 3. Populate initial metadata cache for pickers and badges
      const newSpecMeta: Record<string, { name: string }> = {};
      if (specObj && specId) {
        newSpecMeta[specId] = { name: specObj.name || '' };
      }

      const newSubMeta: Record<string, { name: string }> = {};
      if (subObj && subId) {
        newSubMeta[subId] = { name: subObj.name || '' };
      }

      const newSkillMeta: Record<string, SkillMetadata> = {};
      if (Array.isArray(prof.skills)) {
        prof.skills.forEach((s: any) => {
          if (typeof s === 'object' && s !== null) {
            const sId = s.id || s._id;
            if (sId) {
              newSkillMeta[sId] = {
                name: s.name || '',
                category: Number(s.category) || 0,
              };
            }
          }
        });
      }

      setMetadata({
        specialties: newSpecMeta,
        subspecialties: newSubMeta,
        skills: newSkillMeta,
      });
    }
  }, [user]);

  const updateMeta = (category: keyof SkillsMetadataState, newItems: Record<string, any>) => {
    setMetadata((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        ...newItems,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // Map local picker state to backend PartialProfessionalProfileUpdate contract
      const payload: PartialProfessionalProfileUpdate = {
        specialty: skillsData.specialty_id,
        subspecialty: skillsData.subspecialty_id,
        skills: skillsData.skills,
      };

      const updatedUser = await updateSkills(payload);

      // Local state update fallback if context is not auto-rehydrated
      if (user?.professional) {
        updateProfileField('professional', {
          ...user.professional,
          specialty: updatedUser.professional?.specialty ?? null,
          subspecialty: updatedUser.professional?.subspecialty ?? null,
          skills: updatedUser.professional?.skills ?? [],
        });
      }

      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      console.error(err);
      setError(t(VocabProMenu, 'skills_save_error'));
    } finally {
      setSaving(false);
    }
  };

  const isSaving = saving || isSavingHook;

  return (
    <div className="d-flex flex-column gap-4 animate-in">
      {/* Subheader Bar: Section Description Left | Save Button Right */}
      <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
        <div>
          <p className="m-0 text-muted small">{t(VocabProMenu, 'skills_desc')}</p>
        </div>

        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={isSaving}
          className="d-flex align-items-center gap-2 shadow-sm"
        >
          {isSaving ? <Spinner size="sm" animation="border" /> : <CheckCircle size={16} />}
          <span>{t(btn_vocab, 'save')}</span>
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* SPECIALTY & SUBSPECIALTY SELECTOR */}
      <SpecialtySubSection
        specialtyId={skillsData.specialty_id}
        subspecialtyId={skillsData.subspecialty_id}
        specialtiesMeta={metadata.specialties}
        subspecialtiesMeta={metadata.subspecialties}
        specService={specService}
        onSelectSpecialty={(spec, isClear) => {
          if (isClear) {
            setSkillsData((prev) => ({ ...prev, specialty_id: null, subspecialty_id: null }));
            return;
          }
          if (spec) {
            updateMeta('specialties', { [spec.id]: { name: spec.name } });
            setSkillsData((prev) => ({
              ...prev,
              specialty_id: spec.id,
              subspecialty_id: null,
            }));
          }
        }}
        onSelectSubspecialty={(sub) => {
          if (!sub) {
            setSkillsData((prev) => ({ ...prev, subspecialty_id: null }));
            return;
          }
          updateMeta('subspecialties', { [sub.id]: { name: sub.name } });
          setSkillsData((prev) => ({ ...prev, subspecialty_id: sub.id }));
        }}
      />

      <hr className="my-1" />

      {/* SKILLS MULTI-SELECT & BADGES */}
      <SkillsBadgeSection
        activeSkills={skillsData.skills}
        skillsMeta={metadata.skills}
        skillsService={skillsService}
        onAddSkill={(skill: SkillRead) => {
          const skillId = skill.id;
          const categoryNum = Number(skill.category) || 0;

          updateMeta('skills', {
            [skillId]: {
              name: skill.name,
              category: categoryNum,
            },
          });

          if (!skillsData.skills.includes(skillId)) {
            setSkillsData((prev) => ({
              ...prev,
              skills: [...prev.skills, skillId],
            }));
          }
        }}
        onRemoveSkill={(skillId: string) => {
          setSkillsData((prev) => ({
            ...prev,
            skills: prev.skills.filter((id) => id !== skillId),
          }));
        }}
      />
    </div>
  );
};

export default SkillsWidget;